import { Component, OnInit, Input, HostListener, ViewChild, ElementRef, EventEmitter, Output } from "@angular/core";
import { Router, NavigationEnd } from '@angular/router';
import { SessionService } from "@app/shared/services/session.service";
import { Menu, MenuService } from "@src/app/shared/services/menu.service";
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { filter } from 'rxjs';
import { CompanyService } from "@src/app/shared/backend/clients";

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
    host: {
        class: 'app-sidebar'
    },
    standalone: false
})
export class SidebarComponent implements OnInit {

  @ViewChild('settingsIcon') settingsIcon?: ElementRef<HTMLElement>;

  currentUrl: string = '';
  isMobile: boolean = false;
  companyName: any = '';
  brandingLogoUrl: string = '';
  rotation = 0;

  showUserManualLink: boolean = false;
  showSupportLink: boolean = false;

  userManualUrl: string = '';
  supportUrl: string = '';

  @Input() displaySidebar: boolean = true;
  @Output() closeSidebarEvent = new EventEmitter<void>();

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkMobileView();
  }

  canManageSettings: boolean = false;
  menus: Menu[] = [];

  constructor(
    public session: SessionService,
    private permissionService: PermissionService,
    public menuService: MenuService,
    public companyService: CompanyService,
    private router: Router) { }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.checkMobileView();

    this.canManageSettings = await this.permissionService.hasActionPermissions(Actions.manage_systemsettings);

    this.currentUrl = this.router.url;

    this.session.currentCompanyData$.subscribe(company => {
      this.companyName = company?.name ?? '';
    });

    this.session.brandingLogoUrl$.subscribe((url) => {
      this.brandingLogoUrl = url ?? '';
    });

    this.session.sidebarLinks$.subscribe(links => {
      this.userManualUrl = links.userManualLink;
      this.supportUrl = links.supportLink;

      this.showUserManualLink = !!links.userManualLink?.trim();
      this.showSupportLink = !!links.supportLink?.trim();
    });

    // update currentUrl when navigation ends + auto-expand parent
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentUrl = event.url;
      this.expandParentsForActiveRoute();
    });

    // subscribe to feature updates (sessionService)
    this.session.featuresUpdated$.subscribe(async () => {
      await this.session.getCompanySettings(true); // refresh cached settings once
      this.permissionService.reset(); // rebuild permission caches next warmUp()
      this.menus = await this.menuService.getUserMenus();
      this.expandParentsForActiveRoute();
    });

    setTimeout(async () => {
      this.companyName = (await this.session.getCompanyData())?.name;
      this.menus = await this.menuService.getUserMenus();
      this.expandParentsForActiveRoute();

      const branding = this.session.getBrandingSettings();
      this.brandingLogoUrl = branding?.logoUrl ?? '';

      const company = await this.session.getCompanyData();
      this.applyCompanySidebarLinks(company);
    }, 50);
  }

  ngOnChanges(): void { }

  onNavClick(): void {
    if (this.isMobile) {
      this.closeSidebarEvent.emit();
    }
  }

  checkMobileView() {
    this.displaySidebar = window.innerWidth >= 1200;
    this.isMobile = window.innerWidth < 1200;
  }

  toggleSubmenu(item: Menu) {
    item.isExpanded = !item.isExpanded;
  }

  closeSidebar() {
    this.closeSidebarEvent.emit();
  }

  isActive(menuPath: string): boolean {
    if (!menuPath) return false;

    // Special case - (implicitSale: true)
    // when "Activate warranty" points to "/sales/add"
    // do not mark the main "Sales" menu as active
    if (menuPath === '/sales' && this.currentUrl === '/sales/add') {
      return false;
    }

    return this.currentUrl === menuPath || this.currentUrl.startsWith(menuPath + '/');
  }

  isChildActive(menuPath: string): boolean {
    if (!menuPath) return false;

    return this.currentUrl === menuPath;
  }

  private expandParentsForActiveRoute(): void {
    if (!this.menus?.length) return;

    for (const item of this.menus) {
      if (!item.children?.length) continue;

      const hasActiveChild = item.children.some((child:any) => this.isActive(child.path));

      if (hasActiveChild) {
        item.isExpanded = true;
      }
    }
  }

  goToSettings() {
    this.router.navigate(['/system-settings']);
  }

  rotateIcon(): void {
    if (!this.settingsIcon) return;

    this.rotation += 90;
    this.settingsIcon.nativeElement.style.transform = `rotate(${this.rotation}deg)`;
    this.settingsIcon.nativeElement.style.transition = 'transform 0.3s ease-in-out';
  }

  private applyCompanySidebarLinks(company: any): void {
    this.companyName = company?.name ?? '';
    this.userManualUrl = company?.userManualLink ?? '';
    this.supportUrl = company?.supportLink ?? '';

    this.showUserManualLink = !!this.userManualUrl;
    this.showSupportLink = !!this.supportUrl;
  }
}
